import { MediaGallery } from './media-gallery.js';

/**
 * Photo Print Media Gallery Component
 *
 * Extends MediaGallery to add photo upload integration for print products.
 * Handles:
 * - Loading overlay during variant transitions (prevents flash of default artwork)
 * - Photo upload/removal events
 * - Persisting uploaded photo across variant changes
 */
class PhotoPrintMediaGallery extends MediaGallery {
  #uploadedPhotoUrl = null;
  #photoCropPromise = null;
  _loadingOverlay = null;

  /**
   * Hook called before variant change processing.
   * Shows loading overlay if photo is uploaded.
   */
  _beforeVariantChange(variantId) {
    if (this._hasUploadedPhoto()) {
      this._showGalleryLoading();
      this.#photoCropPromise = this.#waitForPhotoCrop();
    }
  }

  /**
   * Hook to wait for dependencies before updating gallery.
   * Waits for photo to be re-cropped for new aspect ratio.
   */
  async _waitForVariantDependencies() {
    if (this.#photoCropPromise) {
      await this.#photoCropPromise;
      this.#photoCropPromise = null;
    }
  }

  /**
   * Hook called after variant change completes.
   * Hides loading overlay.
   */
  _afterVariantChange() {
    this._hideGalleryLoading();
  }

  /**
   * Returns the uploaded photo URL for use by base class.
   * @returns {string|null} The uploaded photo URL or null.
   */
  _getUploadedPhotoUrl() {
    return this.#uploadedPhotoUrl;
  }

  /**
   * Checks if there's an uploaded photo.
   * @returns {boolean} True if photo is uploaded.
   */
  _hasUploadedPhoto() {
    if (this.#uploadedPhotoUrl) return true;

    const photoUploadComponent = document.querySelector('photo-upload-component');
    return photoUploadComponent && (
      photoUploadComponent.uploadedFile ||
      photoUploadComponent.croppedImageURL ||
      photoUploadComponent.uploadedImageURL
    );
  }

  /**
   * Waits for the photo-upload component to finish re-cropping after a size change.
   * @returns {Promise<void>} Resolves when photo-upload:changed fires or timeout occurs.
   */
  #waitForPhotoCrop() {
    return new Promise((resolve) => {
      const handler = (event) => {
        document.removeEventListener('photo-upload:changed', handler);
        this.#uploadedPhotoUrl = event.detail.imageUrl;
        resolve();
      };

      document.addEventListener('photo-upload:changed', handler);

      // Timeout fallback in case the event never fires
      setTimeout(() => {
        document.removeEventListener('photo-upload:changed', handler);
        resolve();
      }, 1000);
    });
  }

  /**
   * Shows a loading overlay on the gallery during variant transitions.
   */
  _showGalleryLoading() {
    if (!this._loadingOverlay) {
      this._loadingOverlay = document.createElement('div');
      this._loadingOverlay.className = 'media-gallery__loading-overlay';
      this._loadingOverlay.innerHTML = '<div class="media-gallery__loading-spinner"></div>';

      if (!document.getElementById('media-gallery-loading-styles')) {
        const style = document.createElement('style');
        style.id = 'media-gallery-loading-styles';
        style.textContent = `
          .media-gallery__loading-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.85);
            backdrop-filter: blur(4px);
            z-index: 100;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: gallery-loading-fade-in 0.1s ease;
          }
          .media-gallery__loading-spinner {
            width: 32px;
            height: 32px;
            border: 3px solid #e5e5e5;
            border-top-color: #1a1a1a;
            border-radius: 50%;
            animation: gallery-loading-spin 0.7s linear infinite;
          }
          @keyframes gallery-loading-fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes gallery-loading-spin {
            to { transform: rotate(360deg); }
          }
        `;
        document.head.appendChild(style);
      }
    }

    const currentPosition = getComputedStyle(this).position;
    if (currentPosition === 'static') {
      this.style.position = 'relative';
    }

    if (!this._loadingOverlay.parentNode) {
      this.appendChild(this._loadingOverlay);
    }
  }

  /**
   * Hides the loading overlay from the gallery.
   */
  _hideGalleryLoading() {
    if (this._loadingOverlay && this._loadingOverlay.parentNode) {
      this._loadingOverlay.remove();
    }
  }

  /**
   * Handles photo upload - updates gallery to show uploaded photo in mocks.
   */
  #handlePhotoUpload = async (event) => {
    const { imageUrl } = event.detail;
    this.#uploadedPhotoUrl = imageUrl;
    await this._updateGalleryWithUploadedPhoto();
  };

  /**
   * Handles photo removal - reverts gallery to original artwork.
   */
  #handlePhotoRemoved = async () => {
    this.#uploadedPhotoUrl = null;
    await this._updateGalleryWithUploadedPhoto();
  };

  /**
   * Restores uploaded photo from sessionStorage when gallery is re-rendered.
   */
  #restoreUploadedPhotoFromStorage() {
    try {
      const stored = sessionStorage.getItem('uploaded_photo');
      if (!stored) return;

      const photoData = JSON.parse(stored);
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - photoData.timestamp > oneHour) {
        sessionStorage.removeItem('uploaded_photo');
        return;
      }

      // Photo exists - PhotoUploadComponent will dispatch photo-upload:changed
      this._expectingUploadedPhoto = true;
    } catch (e) {
      console.warn('[PhotoPrintMediaGallery] Failed to check sessionStorage:', e);
    }
  }

  /**
   * Updates gallery mocks with uploaded photo or reverts to original artwork.
   */
  async _updateGalleryWithUploadedPhoto() {
    try {
      const allMockContainers = this.querySelectorAll('.mock-image-container');

      allMockContainers.forEach((container) => {
        if (this.#uploadedPhotoUrl) {
          container.dataset.artworkSrc = this.#uploadedPhotoUrl;
          this.#updateContainerArtwork(container, this.#uploadedPhotoUrl);
        } else {
          delete container.dataset.artworkSrc;
          this.#restoreContainerArtwork(container);
        }
        container.dispatchEvent(new CustomEvent('artwork-changed', { bubbles: false }));
      });

      // Also update thumbnails
      const thumbnailContainers = this.querySelectorAll('slideshow-controls .mock-image-container');
      thumbnailContainers.forEach((container) => {
        if (this.#uploadedPhotoUrl) {
          container.dataset.artworkSrc = this.#uploadedPhotoUrl;
          this.#updateContainerArtwork(container, this.#uploadedPhotoUrl);
        } else {
          delete container.dataset.artworkSrc;
          this.#restoreContainerArtwork(container);
        }
        container.dispatchEvent(new CustomEvent('artwork-changed', { bubbles: false }));
      });

      // Update zoom dialog images (rendered without mocks using force_no_mock: true)
      this.#updateZoomDialogImages();

    } catch (error) {
      console.error('[PhotoPrintMediaGallery] Error updating gallery with photo:', error);
    }
  }

  /**
   * Updates the zoom dialog images with the uploaded photo.
   * The zoom dialog renders images directly (not as mocks) so we need to update
   * the img.product-media__image elements inside the dialog-zoomed-gallery.
   */
  #updateZoomDialogImages() {
    const zoomDialog = this.querySelector('zoom-dialog');
    if (!zoomDialog) return;

    // Get the first image in the zoom gallery (corresponds to the artwork/mock slide)
    const zoomGallery = zoomDialog.querySelector('.dialog-zoomed-gallery');
    if (!zoomGallery) return;

    // The first li in the zoom gallery is the artwork image
    const firstZoomItem = zoomGallery.querySelector('li:first-child');
    if (!firstZoomItem) return;

    const zoomImage = firstZoomItem.querySelector('img.product-media__image');
    if (!zoomImage) return;

    if (this.#uploadedPhotoUrl) {
      // Store original src if not already stored
      if (!zoomImage.dataset.originalSrc) {
        zoomImage.dataset.originalSrc = zoomImage.src;
      }
      zoomImage.src = this.#uploadedPhotoUrl;
    } else if (zoomImage.dataset.originalSrc) {
      // Restore original image
      zoomImage.src = zoomImage.dataset.originalSrc;
    }

    // Also update the first thumbnail in the zoom dialog
    const thumbnailsList = zoomDialog.querySelector('.dialog-thumbnails-list');
    if (thumbnailsList) {
      const firstThumbnail = thumbnailsList.querySelector('button:first-child');
      if (firstThumbnail) {
        // Check if thumbnail has a mock container or direct image
        const thumbMockContainer = firstThumbnail.querySelector('.mock-image-container');
        if (thumbMockContainer) {
          if (this.#uploadedPhotoUrl) {
            thumbMockContainer.dataset.artworkSrc = this.#uploadedPhotoUrl;
            this.#updateContainerArtwork(thumbMockContainer, this.#uploadedPhotoUrl);
          } else {
            delete thumbMockContainer.dataset.artworkSrc;
            this.#restoreContainerArtwork(thumbMockContainer);
          }
          thumbMockContainer.dispatchEvent(new CustomEvent('artwork-changed', { bubbles: false }));
        } else {
          // Direct image thumbnail
          const thumbImage = firstThumbnail.querySelector('img');
          if (thumbImage) {
            if (this.#uploadedPhotoUrl) {
              if (!thumbImage.dataset.originalSrc) {
                thumbImage.dataset.originalSrc = thumbImage.src;
                thumbImage.dataset.originalSrcset = thumbImage.srcset || '';
              }
              thumbImage.src = this.#uploadedPhotoUrl;
              thumbImage.removeAttribute('srcset');
            } else if (thumbImage.dataset.originalSrc) {
              thumbImage.src = thumbImage.dataset.originalSrc;
              if (thumbImage.dataset.originalSrcset) {
                thumbImage.srcset = thumbImage.dataset.originalSrcset;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Updates artwork in a container based on its render mode.
   */
  #updateContainerArtwork(container, photoUrl) {
    // CSS mode
    if (container.classList.contains('mock-image-container--css')) {
      const artworkImg = container.querySelector('.mock-image__artwork');
      if (artworkImg) {
        if (!artworkImg.dataset.originalSrc) {
          artworkImg.dataset.originalSrc = artworkImg.src;
          artworkImg.dataset.originalSrcset = artworkImg.srcset || '';
          artworkImg.dataset.originalSizes = artworkImg.sizes || '';
        }
        artworkImg.src = photoUrl;
        artworkImg.removeAttribute('srcset');
        artworkImg.removeAttribute('sizes');
      }
    }

    // CSS3D mode
    if (container.classList.contains('mock-image-container--css3d')) {
      const css3dComponent = container.querySelector('mock-image-css3d');
      if (css3dComponent) {
        const artworkImg = css3dComponent.querySelector('.mock-image__artwork');
        if (artworkImg) {
          if (!artworkImg.dataset.originalSrc) {
            artworkImg.dataset.originalSrc = artworkImg.src;
            artworkImg.dataset.originalSrcset = artworkImg.srcset || '';
            artworkImg.dataset.originalSizes = artworkImg.sizes || '';
          }
          artworkImg.src = photoUrl;
          artworkImg.removeAttribute('srcset');
          artworkImg.removeAttribute('sizes');
        }
      }
    }

    // SVG mode
    if (container.classList.contains('mock-image-container--svg')) {
      const svgComponent = container.querySelector('mock-image-svg');
      if (svgComponent) {
        if (!svgComponent.dataset.originalArtworkSrc) {
          svgComponent.dataset.originalArtworkSrc = svgComponent.dataset.artworkSrc || '';
        }
        if (typeof svgComponent.updateArtwork === 'function') {
          svgComponent.updateArtwork(photoUrl);
        }
      }
    }
  }

  /**
   * Restores original artwork in a container.
   */
  #restoreContainerArtwork(container) {
    // CSS mode
    if (container.classList.contains('mock-image-container--css')) {
      const artworkImg = container.querySelector('.mock-image__artwork');
      if (artworkImg && artworkImg.dataset.originalSrc) {
        artworkImg.src = artworkImg.dataset.originalSrc;
        if (artworkImg.dataset.originalSrcset) {
          artworkImg.srcset = artworkImg.dataset.originalSrcset;
        }
        if (artworkImg.dataset.originalSizes) {
          artworkImg.sizes = artworkImg.dataset.originalSizes;
        }
      }
    }

    // CSS3D mode
    if (container.classList.contains('mock-image-container--css3d')) {
      const css3dComponent = container.querySelector('mock-image-css3d');
      if (css3dComponent) {
        const artworkImg = css3dComponent.querySelector('.mock-image__artwork');
        if (artworkImg && artworkImg.dataset.originalSrc) {
          artworkImg.src = artworkImg.dataset.originalSrc;
          if (artworkImg.dataset.originalSrcset) {
            artworkImg.srcset = artworkImg.dataset.originalSrcset;
          }
          if (artworkImg.dataset.originalSizes) {
            artworkImg.sizes = artworkImg.dataset.originalSizes;
          }
        }
      }
    }

    // SVG mode
    if (container.classList.contains('mock-image-container--svg')) {
      const svgComponent = container.querySelector('mock-image-svg');
      if (svgComponent && svgComponent.dataset.originalArtworkSrc && typeof svgComponent.updateArtwork === 'function') {
        svgComponent.updateArtwork(svgComponent.dataset.originalArtworkSrc);
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Add photo-specific event listeners
    const controller = new AbortController();
    this._photoController = controller;
    const { signal } = controller;

    document.addEventListener('photo-upload:changed', this.#handlePhotoUpload, { signal });
    document.addEventListener('photo-upload:removed', this.#handlePhotoRemoved, { signal });

    // Restore uploaded photo from sessionStorage
    this.#restoreUploadedPhotoFromStorage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._photoController?.abort();
  }
}

// Register custom element
if (!customElements.get('photo-print-media-gallery')) {
  customElements.define('photo-print-media-gallery', PhotoPrintMediaGallery);
}

export default PhotoPrintMediaGallery;
